/**
 * ReporteComercializacion.jsx
 * ─────────────────────────────────────────────────────────────────────
 * Reporte generativo de cartera comercial IFRS 9.
 * Mismo patrón visual que ReporteCaptacion: top bar + pestañas + HTML export.
 */

import React, { useState, useCallback, useEffect, useRef } from "react";
import {
  COLOR, fmt, fmtB, fmtPct, fmtN,
  KpiCard, BarRow, SectionHeader, InsightBox,
  MapaEstados, MapaLeyenda, MX_ISO_TO_CVE,
} from "./ReporteComponents";

const API_URL = process.env.REACT_APP_BACKEND_URL || "https://cnbv-agente-196915693363.us-central1.run.app";

const PERIODOS = ["202607","202606","202605","202604","202603","202602","202601","202512","202511","202510","202509"];

const INSTITUCIONES = [
  "Banco Base",
  "CIBanco",
  "Intercam Banco",
  "Monex",
  "Banca Mifel",
  "Banco del Bajío",
  "Banregio",
  "Interacciones",
  "Multiva",
  "Sabadell",
  "Ve por Más",
  "Accendo Banco",
  "Actinver",
  "Bancrea",
  "Bank of China",
  "Bankaool",
  "Bansí",
  "Covalto",
  "Dondé Banco",
  "ICBC",
  "Inmobiliario Mexicano",
  "KEB Hana México",
  "Kapital",
  "Mizuho Bank",
  "Shinhan",
  "Ualá",
  "Banco Ahorro Famsa",
  "Banco Azteca",
  "Bancoppel",
  "Banfeliz",
  "Compartamos",
  "Consubanco",
  "Hey Banco",
  "BBVA México",
  "Banamex",
  "Banorte",
  "HSBC",
  "Inbursa",
  "Santander",
  "Scotiabank",
  "Afirme",
  "BIAfirme",
  "Banco S3",
  "Bank of America",
  "Citi México",
  "Invex",
  "J.P. Morgan",
  "MUFG Bank",
];

const SECCIONES = [
  { id: "resumen",    icon: "📋", label: "Resumen"     },
  { id: "calidad",    icon: "◉",  label: "Calidad"     },
  { id: "rollrates",  icon: "⟳",  label: "Migración"   },
  { id: "sectorial",  icon: "◆",  label: "Sectorial"   },
  { id: "originacion",icon: "⊕",  label: "Originación" },
  { id: "territorio", icon: "🗺️", label: "Territorio"  },
  { id: "riesgo",     icon: "🔍", label: "Riesgo"      },
  { id: "pronostico", icon: "📈", label: "Pronóstico"  },
];

const fmtImor = n => n == null ? "—" : `${fmt(n, 2)}%`;
const fmtTasa = n => n == null ? "—" : `${fmt(n, 2)}%`;

// ══════════════════════════════════════════════════════════════════════
// GENERADOR HTML EXPORT
// ══════════════════════════════════════════════════════════════════════
function generarHTMLExport(reporte) {
  const s   = reporte.secciones || {};
  const k   = s.resumen?.kpis || {};
  const per = String(reporte.periodo);
  const perFmt = `${per.slice(0,4)}-${per.slice(4)}`;
  const ent = reporte.nombre_entidad || "Banca Múltiple";

  const fB  = (n, d=2) => `${(n ?? 0).toFixed(d)} mmdp`;
  const fN  = n => (n ?? 0).toLocaleString("es-MX");
  const fP  = n => n == null ? "—" : `${n >= 0 ? "+" : ""}${(n ?? 0).toFixed(1)}%`;
  const fI  = n => n == null ? "—" : `${(n ?? 0).toFixed(2)}%`;
  const esc = v => String(v ?? "").replace(/&/g,"&amp;").replace(/</g,"&lt;").replace(/>/g,"&gt;");

  const C = {
    bg:"#070c12", surface:"#0d1520", card:"#111b28", border:"#1a2535",
    text:"#dde4ee", muted:"#8aa4b8", dim:"#556a7a",
    gold:"#c8a96e", goldDim:"#8a6d3b",
    green:"#34d399", red:"#f87171", blue:"#60a5fa",
  };

  const kpiCard = (label, value, delta, dl, accent) =>
    `<div style="background:${C.card};border-radius:6px;padding:12px 16px;border:1px solid ${C.border};flex:1;min-width:160px;">
      <div style="font-size:10px;color:${C.muted};font-weight:700;font-family:monospace;text-transform:uppercase;letter-spacing:.6px;margin-bottom:6px;">${esc(label)}</div>
      <div style="font-size:22px;font-weight:800;color:${accent||C.gold};font-family:monospace;">${esc(value)}</div>
      ${delta != null ? `<div style="font-size:11px;margin-top:5px;font-family:monospace;color:${delta>0?C.green:delta<0?C.red:C.muted};">${delta>0?"▲":delta<0?"▼":"─"} ${fP(delta)}${dl?` <span style="color:${C.dim}">${esc(dl)}</span>`:""}</div>` : ""}
    </div>`;

  const insightBox = text => !text ? "" :
    `<div style="background:${C.gold}0d;border:1px solid ${C.goldDim};border-radius:6px;padding:10px 14px;font-size:12px;line-height:1.7;color:${C.text};margin-top:10px;">
      <div style="font-size:9px;font-weight:700;font-family:monospace;background:${C.gold}22;color:${C.gold};display:inline-block;padding:1px 8px;border-radius:4px;margin-bottom:6px;border:1px solid ${C.goldDim};">✦ Comentario analítico</div>
      <div style="white-space:pre-wrap;">${esc(text)}</div>
    </div>`;

  const secHeader = (icon, title) =>
    `<div style="padding:10px 0;margin-bottom:14px;border-bottom:2px solid ${C.gold}44;">
      <span style="font-size:16px;margin-right:8px;">${icon}</span>
      <span style="font-weight:700;font-size:13px;color:${C.gold};font-family:monospace;letter-spacing:0.05em;text-transform:uppercase;">${esc(title)}</span>
    </div>`;

  const barRow = (label, pct, right, color) =>
    `<div style="display:flex;align-items:center;gap:8px;margin-bottom:6px;">
      <div style="width:130px;font-size:11px;color:${C.muted};font-family:monospace;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;flex-shrink:0;">${esc(label)}</div>
      <div style="flex:1;height:6px;background:${C.border};border-radius:4px;overflow:hidden;"><div style="width:${Math.min(100,Math.max(1,pct))}%;height:100%;background:${color||C.gold};border-radius:4px;"></div></div>
      <div style="font-size:11px;color:${C.muted};width:80px;font-family:monospace;text-align:right;flex-shrink:0;">${esc(right)}</div>
    </div>`;

  const thS = `padding:8px 12px;text-align:left;color:${C.muted};font-weight:600;border-bottom:1px solid ${C.border};font-size:10px;font-family:monospace;`;
  const tdS = `padding:7px 12px;font-size:10px;font-family:monospace;`;

  // 1. Resumen
  const secResumen = `<section style="margin-bottom:32px;">
    ${secHeader("📋","Resumen ejecutivo")}
    <div style="display:flex;gap:10px;margin-bottom:10px;flex-wrap:wrap;">
      ${kpiCard("Saldo total",       fB(k.saldo_total_b),   k.delta_total_mom, "MoM")}
      ${kpiCard("Cartera vigente",   fB(k.saldo_vigente_b))}
      ${kpiCard("Cartera vencida",   fB(k.saldo_vencido_b), k.delta_venc_mom,  "MoM", C.red)}
      ${kpiCard("IMOR",              fI(k.imor),            null, null, k.imor>3?C.red:C.gold)}
      ${kpiCard("ICOR",              fI(k.icor))}
    </div>
    <div style="display:flex;gap:10px;margin-bottom:10px;flex-wrap:wrap;">
      ${kpiCard("Núm. créditos",     fN(k.num_creditos))}
      ${kpiCard("Saldo promedio",    `$${fN(k.saldo_promedio_mxn)} MXN`)}
      ${kpiCard("Tasa ponderada",    fI(k.tasa_ponderada))}
      ${kpiCard("Plazo ponderado",   `${fmt(k.plazo_ponderado_meses,0)} meses`)}
    </div>
    <div style="display:flex;gap:10px;margin-bottom:16px;flex-wrap:wrap;">
      ${kpiCard("Crecimiento YoY nominal", fP(k.delta_total_yoy),       null, null, k.delta_total_yoy>0?C.green:C.red)}
      ${k.delta_total_yoy_real!=null ? kpiCard("Crecimiento YoY real", fP(k.delta_total_yoy_real), null, null, k.delta_total_yoy_real>0?C.green:C.red) : ""}
      ${kpiCard("Saldo MN",          fB(k.saldo_mn_b), null, null, C.blue)}
      ${kpiCard("Saldo ME",          fB(k.saldo_me_b))}
    </div>
    ${insightBox(s.resumen?.narrativa)}
  </section>`;

  // 2. Calidad
  const cal = s.calidad || {};
  const etapas = cal.por_etapa || {};
  const secCalidad = `<section style="margin-bottom:32px;">
    ${secHeader("◉","Calidad de Cartera — IFRS 9")}
    <div style="display:flex;gap:10px;margin-bottom:16px;flex-wrap:wrap;">
      ${kpiCard("Etapa 1 — Normal",         fB(etapas.etapa1?.saldo_b), etapas.etapa1?.delta_mom, "MoM", C.green)}
      ${kpiCard("Etapa 2 — Deterioro",      fB(etapas.etapa2?.saldo_b), etapas.etapa2?.delta_mom, "MoM", C.gold)}
      ${kpiCard("Etapa 3 — Vencida (IMOR)", fB(etapas.etapa3?.saldo_b), etapas.etapa3?.delta_mom, "MoM", C.red)}
    </div>
    ${(cal.por_cartera||[]).length > 0 ? `
    <table style="width:100%;border-collapse:collapse;">
      <thead><tr>
        <th style="${thS}">Tipo de cartera</th>
        <th style="${thS}text-align:right;">Saldo</th>
        <th style="${thS}text-align:right;">Vencido</th>
        <th style="${thS}text-align:right;">IMOR</th>
      </tr></thead>
      <tbody>${(cal.por_cartera||[]).map(c => `
        <tr style="border-bottom:1px solid ${C.border}33;">
          <td style="${tdS}color:${C.text};">${esc(c.cartera)}</td>
          <td style="${tdS}text-align:right;color:${C.muted};">${fB(c.saldo_b)}</td>
          <td style="${tdS}text-align:right;color:${C.muted};">${fB(c.vencido_b)}</td>
          <td style="${tdS}text-align:right;color:${c.imor>3?C.red:C.gold};font-weight:700;">${fI(c.imor)}</td>
        </tr>`).join("")}
      </tbody>
    </table>` : ""}
  </section>`;

  // 3. Migración de saldos por etapa
  const rr  = s.migracion_saldos || s.roll_rates || {};
  const mat = rr.matriz_actual || {};
  const secRollRates = `<section style="margin-bottom:32px;">
    ${secHeader("⟳","Migración de Saldos por Etapa IFRS 9")}
    <div style="display:flex;gap:10px;margin-bottom:16px;flex-wrap:wrap;">
      ${kpiCard("E1 → E2 (deterioro inicial)",    mat.roll_e1_e2_pct!=null?`${fmt(mat.roll_e1_e2_pct,4)}%`:"—", null, null, C.gold)}
      ${kpiCard("E2 → E3 (deterioro confirmado)", mat.roll_e2_e3_pct!=null?`${fmt(mat.roll_e2_e3_pct,4)}%`:"—", null, null, C.red)}
      ${kpiCard("E3 → recuperación (cura)",       mat.cure_rate_pct!=null ?`${fmt(mat.cure_rate_pct,4)}%`:"—",  null, null, C.green)}
    </div>
    ${insightBox(rr.narrativa)}
    <div style="font-size:9px;color:${C.dim};font-family:monospace;margin-top:8px;">* Tasas implícitas calculadas sobre variación de saldos agregados entre periodos consecutivos, no sobre cohortes individuales de crédito.</div>
  </section>`;

  // 4. Sectorial (Actividad + Tamaño + Destino fusionados con narrativa)
  const secData = s.sectorial || {};
  const act  = secData.actividad || s.actividad || {};
  const tam  = secData.tamano    || s.tamano    || [];
  const dest = secData.destino   || s.destino   || {};

  const subH = (t) => `<div style="font-size:10px;font-weight:700;color:${C.muted};font-family:monospace;text-transform:uppercase;letter-spacing:.5px;margin-bottom:10px;padding-bottom:6px;border-bottom:1px solid ${C.border};">${t}</div>`;

  const secSectorial = `<section style="margin-bottom:32px;">
    ${secHeader("◆","Análisis Sectorial")}
    <div style="display:grid;grid-template-columns:1fr 1fr 1fr;gap:20px;margin-bottom:16px;">
      <div>
        ${subH("Actividad Económica — Top por saldo")}
        ${(act.top_saldo||[]).map(a => {
          const max = act.top_saldo[0]?.saldo_b||1;
          return barRow(a.actividad, (a.saldo_b/max)*100, `${fB(a.saldo_b,1)} · ${fI(a.imor)}`, C.blue);
        }).join("")}
        <div style="margin-top:10px;">${subH("Top por IMOR")}</div>
        ${(act.top_imor||[]).map(a => {
          const max = act.top_imor[0]?.imor||1;
          return barRow(a.actividad, (a.imor/max)*100, fI(a.imor), a.imor>3?C.red:C.gold);
        }).join("")}
      </div>
      <div>
        ${subH("Tamaño de Empresa")}
        <table style="width:100%;border-collapse:collapse;">
          <thead><tr>
            <th style="${thS}">Segmento</th>
            <th style="${thS}text-align:right;">Saldo</th>
            <th style="${thS}text-align:right;">IMOR</th>
            <th style="${thS}text-align:right;">Tasa</th>
          </tr></thead>
          <tbody>${tam.map(t => `
            <tr style="border-bottom:1px solid ${C.border}33;">
              <td style="${tdS}color:${C.text};">${esc(t.segmento)}</td>
              <td style="${tdS}text-align:right;color:${C.muted};">${fB(t.saldo_b)}</td>
              <td style="${tdS}text-align:right;color:${t.imor>3?C.red:C.gold};font-weight:700;">${fI(t.imor)}</td>
              <td style="${tdS}text-align:right;color:${C.muted};">${fI(t.tasa_ponderada)}</td>
            </tr>`).join("")}
          </tbody>
        </table>
      </div>
      <div>
        ${subH("Destino de Crédito — Top por saldo")}
        ${(dest.top_saldo||[]).map(d => {
          const max = dest.top_saldo[0]?.saldo_b||1;
          return barRow(d.destino, (d.saldo_b/max)*100, fB(d.saldo_b,1), C.blue);
        }).join("")}
        <div style="margin-top:10px;">${subH("Top por IMOR")}</div>
        ${(dest.top_imor||[]).map(d => {
          const max = dest.top_imor[0]?.imor||1;
          return barRow(d.destino, (d.imor/max)*100, fI(d.imor), d.imor>3?C.red:C.gold);
        }).join("")}
      </div>
    </div>
    ${insightBox(secData.narrativa)}
  </section>`;

  // 5. Originación de crédito
  const orig = s.originacion || {};
  const origK = orig.kpis || {};
  const secOriginacion = `<section style="margin-bottom:32px;">
    ${secHeader("⊕","Originación de Crédito")}
    <div style="display:flex;gap:10px;margin-bottom:16px;flex-wrap:wrap;">
      ${kpiCard("Nuevas cuentas",   fN(origK.nuevas_cuentas||0), origK.delta_cuentas_mom, "MoM")}
      ${kpiCard("Saldo originado",  fB(origK.saldo_b||0),         origK.delta_saldo_mom,   "MoM")}
      ${kpiCard("Saldo promedio",   `$${fN(Math.round(origK.saldo_promedio_mxn||0))} MXN`)}
      ${kpiCard("Tasa ponderada",   origK.tasa_ponderada!=null?`${fmt(origK.tasa_ponderada,2)}%`:"—")}
      ${kpiCard("Monto dispuesto",  fB(origK.monto_dispuesto_b||0),null,null,C.muted)}
    </div>
    <div style="display:grid;grid-template-columns:1fr 1fr 1fr;gap:20px;margin-bottom:16px;">
      <div>
        ${subH("Top Actividad Económica")}
        ${(orig.por_actividad||[]).map(a => {
          const max = (orig.por_actividad[0]?.saldo_b)||1;
          return barRow(a.actividad, (a.saldo_b/max)*100,
            `${fB(a.saldo_b,2)} · ${fN(a.nuevas_cuentas)} ctas`, C.blue);
        }).join("")}
      </div>
      <div>
        ${subH("Por Tamaño de Empresa")}
        ${(orig.por_tamano||[]).map(t => {
          const max = (orig.por_tamano[0]?.saldo_b)||1;
          return `<div style="margin-bottom:8px;">
            <div style="display:flex;justify-content:space-between;font-size:10px;font-family:monospace;margin-bottom:2px;">
              <span style="color:${C.text};">${esc(t.segmento)}</span>
              <span style="color:${C.gold};font-weight:700;">${(t.pct_saldo||0).toFixed(1)}%</span>
            </div>
            <div style="height:4px;background:${C.border};border-radius:2px;">
              <div style="height:100%;background:${C.blue};border-radius:2px;width:${Math.min(100,(t.saldo_b/max)*100)}%;"></div>
            </div>
            <div style="font-size:9px;color:${C.dim};font-family:monospace;margin-top:1px;">
              ${fB(t.saldo_b,2)} · ${fN(t.numero_acreditados||0)} acred. · ${fN(t.nuevas_cuentas)} créd.
            </div>
          </div>`;
        }).join("")}
      </div>
      <div>
        ${subH("Top Instituciones")}
        ${(orig.por_institucion||[]).slice(0,8).map((inst,i) => {
          const max = (orig.por_institucion[0]?.saldo_b)||1;
          return barRow(`${i+1}. ${inst.nombre_entidad}`, (inst.saldo_b/max)*100,
            fB(inst.saldo_b,2), C.blue);
        }).join("")}
      </div>
    </div>
    ${insightBox(orig.narrativa)}
  </section>`;

  // 7. Territorio — mapa coroplético interactivo (igual que captación)
  const terr     = Array.isArray(s.territorio) ? s.territorio : [];
  const munRaw   = s.territorio_municipio;
  const munDatos = Array.isArray(munRaw) ? munRaw : (munRaw?.datos || []);
  const munAdv   = munRaw?.advertencia || null;

  const terrJSON       = JSON.stringify(terr);
  const isoMapJSON     = JSON.stringify(MX_ISO_TO_CVE);
  const TERR_METRICS_COM = [
    { key: "saldo_b",   label: "Saldo total",     fmt: "b"   },
    { key: "vencido_b", label: "Cartera vencida",  fmt: "b"   },
    { key: "imor",      label: "IMOR",             fmt: "pct" },
  ];
  const terrMetricsJSON = JSON.stringify(TERR_METRICS_COM);

  const secTerritorio = `<section style="margin-bottom:32px;">
    ${secHeader("🗺️","Territorio — Distribución por Estado")}

    <!-- Toggle de métricas -->
    <div style="display:flex;gap:6px;flex-wrap:wrap;padding:10px 12px;background:${C.surface};border:1px solid ${C.border};border-radius:8px;margin-bottom:14px;align-items:center;">
      <span style="font-size:10px;color:${C.dim};font-family:monospace;margin-right:4px;">MÉTRICA</span>
      ${TERR_METRICS_COM.map(m => `
      <button class="terr-btn-com" data-metric="${m.key}"
        onclick="switchTerrMetricCom('${m.key}')"
        style="padding:4px 12px;border-radius:4px;font-size:10px;font-family:monospace;cursor:pointer;
               border:1px solid ${m.key==='saldo_b'?C.goldDim:C.border};
               background:${m.key==='saldo_b'?C.gold+'22':'transparent'};
               color:${m.key==='saldo_b'?C.gold:C.muted};
               font-weight:${m.key==='saldo_b'?700:400};">
        ${m.label}
      </button>`).join("")}
    </div>

    <div style="display:grid;grid-template-columns:1fr 1fr;gap:20px;margin-bottom:24px;">
      <!-- MAPA -->
      <div>
        <div style="font-size:10px;font-family:monospace;color:${C.muted};margin-bottom:6px;">
          Mostrando: <span id="terr-metric-label-com" style="color:${C.gold};font-weight:700;">Saldo total</span>
        </div>
        <div id="mapa-estados-com" style="height:380px;border-radius:8px;border:1px solid ${C.border};background:#0a1118;"></div>
        <div style="display:flex;gap:8px;align-items:center;margin-top:8px;font-size:10px;font-family:monospace;color:${C.muted};">
          <span>Bajo</span>
          <div style="width:140px;height:8px;background:linear-gradient(to right,#1e3a8a,#0891b2,#0d9488,#16a34a,#ca8a04,#ea580c,#dc2626);border-radius:4px;"></div>
          <span>Máximo</span>
          <div style="width:10px;height:10px;background:#252836;opacity:0.5;border-radius:2px;margin-left:8px;"></div>
          <span>Sin datos</span>
        </div>
      </div>
      <!-- RANKING -->
      <div style="background:${C.card};border:1px solid ${C.border};border-radius:8px;overflow:hidden;display:flex;flex-direction:column;">
        <div style="padding:8px 14px;background:${C.surface};border-bottom:1px solid ${C.border};font-size:11px;font-family:monospace;color:${C.text};font-weight:600;">
          Ranking — <span id="ranking-metric-label-com">Saldo total</span>
        </div>
        <div id="ranking-list-com" style="flex:1;overflow-y:auto;max-height:380px;"></div>
      </div>
    </div>

    <!-- Tabla de municipios -->
    <div style="font-size:10px;font-weight:700;color:${C.muted};font-family:monospace;text-transform:uppercase;margin-bottom:10px;">Top 10 municipios por saldo</div>
    ${munAdv ? `<div style="font-size:9px;color:${C.dim};font-family:monospace;margin-bottom:6px;">⚠ ${esc(munAdv)}</div>` : ""}
    <table style="width:100%;border-collapse:collapse;">
      <thead><tr>
        <th style="${thS}">Municipio</th>
        <th style="${thS}">Estado</th>
        <th style="${thS}text-align:right;">Saldo</th>
        <th style="${thS}text-align:right;">IMOR</th>
      </tr></thead>
      <tbody>${munDatos.slice(0,10).map(m => `
        <tr style="border-bottom:1px solid ${C.border}33;">
          <td style="${tdS}color:${C.text};">${esc(m.municipio||"")}</td>
          <td style="${tdS}color:${C.muted};">${esc(m.estado||"")}</td>
          <td style="${tdS}text-align:right;color:${C.muted};">${fB(m.saldo_b,3)}</td>
          <td style="${tdS}text-align:right;color:${m.imor>3?C.red:C.gold};font-weight:700;">${fI(m.imor)}</td>
        </tr>`).join("")}
      </tbody>
    </table>
    ${munDatos.length === 0 ? `<div style="font-size:11px;color:${C.dim};font-family:monospace;padding:8px 0;">Sin datos de municipio disponibles.</div>` : ""}
  </section>`;

  // ── SCRIPT DEL MAPA (Comercialización) ──────────────────────────────
  const mapScriptCom = `
var _terrDataCom=${terrJSON};
var _isoMapCom=${isoMapJSON};
var _metricsCom=${terrMetricsJSON};
var _currentMetricCom='saldo_b';
var _geoLayerCom=null;
var _terrIdxCom={};
var _aliasCom={
  "michoacan de ocampo":"michoacan",
  "coahuila de zaragoza":"coahuila",
  "veracruz de ignacio de la llave":"veracruz",
  "ciudad de mexico":"distrito federal",
  "michoacan":"michoacan de ocampo",
  "coahuila":"coahuila de zaragoza",
  "veracruz":"veracruz de ignacio de la llave",
  "distrito federal":"ciudad de mexico"
};

function _normCom(s){
  return (s||"").toLowerCase()
    .normalize("NFD").replace(/[\\u0300-\\u036f]/g,"")
    .replace(/[^a-z ]/g,"").trim().replace(/\\s+/g," ");
}

_terrDataCom.forEach(function(d){
  var key=_normCom(d.estado||"");
  if(key){
    _terrIdxCom[key]=d;
    var alt=_aliasCom[key];
    if(alt) _terrIdxCom[alt]=d;
  }
});

function _lookupFeatCom(f){
  var rawName=_normCom(
    f.properties.ESTADO||f.properties.estado||f.properties.name||f.properties.NAME||""
  );
  return _terrIdxCom[rawName]||_terrIdxCom[_aliasCom[rawName]]||null;
}

function _sortedValsCom(metric){
  return _terrDataCom.map(function(d){return d[metric]||0;}).filter(function(v){return v>0;}).sort(function(a,b){return a-b;});
}
function _csCom(v,sv){
  if(!v||v<=0||!sv.length)return'#1a2535';
  var lo=0,hi=sv.length-1;
  while(lo<hi){var mid=(lo+hi)>>1;if(sv[mid]<v)lo=mid+1;else hi=mid;}
  var p=sv.length>1?lo/(sv.length-1):1;
  return p<0.08?'#1e3a8a':p<0.20?'#0369a1':p<0.34?'#0891b2':p<0.50?'#0d9488':p<0.65?'#16a34a':p<0.80?'#ca8a04':p<0.92?'#ea580c':'#dc2626';
}
function _rankColorCom(i,total){
  var p=total>1?(total-1-i)/(total-1):1;
  return p<0.08?'#1e3a8a':p<0.20?'#0369a1':p<0.34?'#0891b2':p<0.50?'#0d9488':p<0.65?'#16a34a':p<0.80?'#ca8a04':p<0.92?'#ea580c':'#dc2626';
}
function _fmtValCom(val,fmt){
  if(fmt==='b') return (val||0).toFixed(2)+' mmdp';
  if(fmt==='pct') return (val||0).toFixed(2)+'%';
  return (val||0).toLocaleString('es-MX');
}
function _tipHtmlCom(d,name){
  var nd=d||{};
  return '<b style="font-family:monospace">'+(nd.estado||name)+'</b><br>'
    +'<span style="color:#c8a96e">Saldo: '+_fmtValCom(nd.saldo_b,'b')+'</span><br>'
    +'Vencido: '+_fmtValCom(nd.vencido_b,'b')+'<br>'
    +'IMOR: '+_fmtValCom(nd.imor,'pct')+'<br>'
    +'Créditos: '+(nd.num_creditos||0).toLocaleString('es-MX');
}
function _buildRankingCom(metric){
  var mDef=_metricsCom.find(function(m){return m.key===metric;})||_metricsCom[0];
  var sorted=[..._terrDataCom].sort(function(a,b){return (b[metric]||0)-(a[metric]||0);});
  var maxV=sorted.length&&sorted[0][metric]?sorted[0][metric]:1;
  var total=sorted.length;
  return sorted.map(function(e,i){
    var val=e[metric]||0,pct=maxV>0?(val/maxV)*100:0;
    var color;
    if(metric==='imor') color=val>3?'#f87171':val>2?'#fbbf24':'#34d399';
    else color=_rankColorCom(i,total);
    return '<div style="display:flex;align-items:center;gap:8px;padding:5px 14px;border-bottom:1px solid #1a253522;background:'+(i<3?color+'10':'transparent')+';">'
      +'<span style="font-size:10px;font-family:monospace;width:20px;text-align:right;color:'+(i<3?color:'#556a7a')+';font-weight:'+(i<3?700:400)+';"> '+(i+1)+'</span>'
      +'<div style="width:160px;flex-shrink:0;overflow:hidden;"><div style="font-size:11px;font-family:monospace;color:'+(i<3?'#dde4ee':'#8aa4b8')+';white-space:nowrap;overflow:hidden;text-overflow:ellipsis;">'+(e.estado||'')+'</div></div>'
      +'<div style="flex:1;height:5px;background:#1a2535;border-radius:3px;overflow:hidden;"><div style="width:'+Math.max(1,pct)+'%;height:100%;background:'+color+';border-radius:3px;"></div></div>'
      +'<span style="font-size:10px;font-family:monospace;width:90px;text-align:right;color:'+color+';">'+_fmtValCom(val,mDef.fmt)+'</span></div>';
  }).join('');
}

function switchTerrMetricCom(metric){
  _currentMetricCom=metric;
  var mDef=_metricsCom.find(function(m){return m.key===metric;})||_metricsCom[0];
  document.querySelectorAll('.terr-btn-com').forEach(function(btn){
    var active=btn.dataset.metric===metric;
    btn.style.background=active?'#c8a96e22':'transparent';
    btn.style.color=active?'#c8a96e':'#8aa4b8';
    btn.style.border='1px solid '+(active?'#8a6d3b':'#1a2535');
    btn.style.fontWeight=active?700:400;
  });
  var lbl=document.getElementById('terr-metric-label-com');
  if(lbl) lbl.textContent=mDef.label;
  var rlbl=document.getElementById('ranking-metric-label-com');
  if(rlbl) rlbl.textContent=mDef.label;
  var rl=document.getElementById('ranking-list-com');
  if(rl) rl.innerHTML=_buildRankingCom(metric);
  if(_geoLayerCom){
    var sv=_sortedValsCom(metric);
    _geoLayerCom.setStyle(function(f){
      var d=_lookupFeatCom(f);
      if(!d||!(d[metric]>0))return{fillColor:'#252836',fillOpacity:0.35,color:'#2a2f40',weight:0.5};
      return{fillColor:_csCom(d[metric],sv),fillOpacity:0.85,color:'#070c12',weight:0.8};
    });
    _geoLayerCom.eachLayer(function(layer){
      var f=layer.feature,d=_lookupFeatCom(f);
      if(d&&layer.getTooltip) layer.setTooltipContent(_tipHtmlCom(d,f.properties.name||''));
    });
  }
}

(function(){
  var rl=document.getElementById('ranking-list-com');
  if(rl) rl.innerHTML=_buildRankingCom('saldo_b');
  var map=L.map('mapa-estados-com',{zoomControl:true,attributionControl:false}).setView([23.5,-102],5);
  L.tileLayer('https://{s}.basemaps.cartocdn.com/dark_nolabels/{z}/{x}/{y}{r}.png',{maxZoom:10}).addTo(map);
  fetch('https://raw.githubusercontent.com/angelnmara/geojson/master/mexicoHigh.json')
    .then(function(r){return r.json();})
    .then(function(geo){
      var sv=_sortedValsCom('saldo_b');
      _geoLayerCom=L.geoJSON(geo,{
        style:function(f){
          var d=_lookupFeatCom(f);
          if(!d||!d.saldo_b)return{fillColor:'#252836',fillOpacity:0.35,color:'#2a2f40',weight:0.5};
          return{fillColor:_csCom(d.saldo_b,sv),fillOpacity:0.85,color:'#070c12',weight:0.8};
        },
        onEachFeature:function(f,layer){
          var d=_lookupFeatCom(f);
          layer.bindTooltip(d?_tipHtmlCom(d,f.properties.name||''):(f.properties.name||''),{sticky:true});
          layer.on('mouseover',function(){layer.setStyle({weight:2,color:'#c8a96e',fillOpacity:1});});
          layer.on('mouseout',function(){if(_geoLayerCom)_geoLayerCom.resetStyle(layer);});
        }
      }).addTo(map);
    }).catch(function(){});
})();`;

  // 8. Concentración y anomalías
  const conc = s.concentracion || {};
  const anom = s.anomalias || {};
  const secRiesgo = `<section style="margin-bottom:32px;">
    ${secHeader("🔍","Concentración y Anomalías")}
    <div style="display:grid;grid-template-columns:1fr 1fr;gap:20px;">
      <div>
        <div style="font-size:10px;font-weight:700;color:${C.muted};font-family:monospace;text-transform:uppercase;margin-bottom:8px;">Concentración HHI</div>
        <div style="font-size:10px;color:${C.dim};font-family:monospace;line-height:1.6;margin-bottom:10px;background:${C.surface};border:1px solid ${C.border};border-radius:4px;padding:6px 10px;">El Índice Herfindahl-Hirschman (HHI) mide la concentración de mercado sumando el cuadrado de las participaciones de cada institución. Escala: 0–10,000. Valores &lt; 1,500 indican mercado competitivo; entre 1,500 y 2,500, concentración moderada; &gt; 2,500, alta concentración. Un HHI de 10,000 significa que un solo participante controla el 100% del segmento.</div>
        ${conc.insight ? `<p style="font-size:11px;color:${C.muted};line-height:1.6;margin-bottom:10px;">${esc(conc.insight)}</p>` : ""}
        <table style="width:100%;border-collapse:collapse;">
          <thead><tr>
            <th style="${thS}">Estado</th>
            <th style="${thS}text-align:right;">HHI</th>
            <th style="${thS}">Categoría</th>
          </tr></thead>
          <tbody>${(conc.filas||[]).slice(0,5).map(f => `
            <tr style="border-bottom:1px solid ${C.border}33;">
              <td style="${tdS}color:${C.text};">${esc(f.descripcion_estado||"")}</td>
              <td style="${tdS}text-align:right;color:${C.gold};font-weight:700;">${Math.round(f.hhi_actual||0)}</td>
              <td style="${tdS}color:${f.categoria_hhi==="Alta"?C.red:C.gold};">${esc(f.categoria_hhi||"")}</td>
            </tr>`).join("")}
          </tbody>
        </table>
      </div>
      <div>
        <div style="font-size:10px;font-weight:700;color:${C.muted};font-family:monospace;text-transform:uppercase;margin-bottom:8px;">Anomalías estadísticas</div>
        <div style="font-size:10px;color:${C.dim};font-family:monospace;line-height:1.6;margin-bottom:10px;background:${C.surface};border:1px solid ${C.border};border-radius:4px;padding:6px 10px;">Detección de deterioro acelerado de cartera por z-score del delta mensual de IMOR. Para cada par (institución, actividad económica) con saldo ≥ 500 MDP, se calcula la media y desviación estándar histórica del cambio mensual en IMOR. Se reportan casos donde el cambio actual supera 2 desviaciones estándar respecto al patrón histórico y el delta absoluto es ≥ 0.5 pp. Z &gt; 0 indica deterioro; Z &lt; 0 indica mejora.</div>
        ${(anom.filas||[]).slice(0,5).map(a => `
          <div style="padding:6px 0;border-bottom:1px solid ${C.border}33;">
            <div style="font-size:11px;color:${C.text};font-family:monospace;margin-bottom:1px;">${esc(a.nombre_entidad||"")}</div>
            <div style="font-size:10px;color:${C.dim};font-family:monospace;margin-bottom:2px;">${esc(a.actividad_economica||a.descripcion_estado||"")}</div>
            <div style="font-size:10px;color:${C.muted};font-family:monospace;">
              IMOR ${fI(a.imor)} · Δ ${a.delta_imor_pp!=null?(a.delta_imor_pp>0?"+":"")+fI(a.delta_imor_pp)+" pp":"—"}
              · z=${fmt(a.z_score,1)} · <span style="color:${a.z_score>2.5?C.red:a.z_score>2?C.gold:C.green};">${esc(a.tipo_anomalia||"")}</span>
            </div>
          </div>`).join("")}
      </div>
    </div>
  </section>`;

  return `<!DOCTYPE html>
<html lang="es">
<head>
<meta charset="UTF-8"/>
<meta name="viewport" content="width=device-width,initial-scale=1"/>
<title>Cartera Comercial ${ent} — ${perFmt}</title>
<link rel="stylesheet" href="https://unpkg.com/leaflet@1.9.4/dist/leaflet.css"/>
<script src="https://unpkg.com/leaflet@1.9.4/dist/leaflet.js"><\/script>
<style>
  *{box-sizing:border-box;margin:0;padding:0}
  body{background:${C.bg};color:${C.text};font-family:'DM Sans',system-ui,sans-serif;padding:32px 40px;max-width:1100px;margin:auto;}
  h1{font-size:20px;font-weight:800;color:${C.gold};font-family:monospace;letter-spacing:.05em;}
  @media print{body{padding:16px}section{page-break-inside:avoid}#mapa-estados-com{height:280px!important;}}
  .leaflet-tooltip{background:#0d1520;border:1px solid #1a2535;color:#dde4ee;font-family:monospace;font-size:11px;}
</style>
</head>
<body>
  <div style="margin-bottom:28px;padding-bottom:16px;border-bottom:2px solid ${C.gold}44;">
    <h1>◈ CARTERA COMERCIAL IFRS 9</h1>
    <div style="font-size:13px;color:${C.muted};font-family:monospace;margin-top:6px;">
      ${esc(ent)} · Periodo ${esc(perFmt)} · Generado ${new Date().toLocaleString("es-MX")}
    </div>
  </div>
  ${secResumen}
  ${secCalidad}
  ${secRollRates}
  ${secSectorial}
  ${secOriginacion}
  ${secTerritorio}
  ${secRiesgo}
  <div style="margin-top:40px;padding-top:16px;border-top:1px solid ${C.border};font-size:9px;color:${C.dim};font-family:monospace;">
    Reporte generado automáticamente por EMRISK · CNBV · Datos: Banca Múltiple sector=40 · Vista: cnbv_com_saldo_vw
  </div>
  <script>${mapScriptCom}<\/script>
</body>
</html>`;
}

// ══════════════════════════════════════════════════════════════════════
// CONTENIDO POR SECCIÓN
// ══════════════════════════════════════════════════════════════════════

function SeccionResumen({ s }) {
  const k = s?.resumen?.kpis || {};
  return (
    <div>
      <div style={{ display: "flex", gap: 10, marginBottom: 10, flexWrap: "wrap" }}>
        <KpiCard label="Saldo Total"     value={fmtB(k.saldo_total_b)}   unit="" delta={k.delta_total_mom}  deltaLabel="MoM" />
        <KpiCard label="Cartera Vigente" value={fmtB(k.saldo_vigente_b)} unit="" />
        <KpiCard label="Cartera Vencida" value={fmtB(k.saldo_vencido_b)} unit="" delta={k.delta_venc_mom}   deltaLabel="MoM" accentColor={COLOR.red} />
        <KpiCard label="IMOR"            value={fmtImor(k.imor)}         unit="" accentColor={k.imor > 3 ? COLOR.red : k.imor > 2 ? COLOR.gold : COLOR.green} />
        <KpiCard label="ICOR"            value={fmtImor(k.icor)}         unit="" accentColor={COLOR.muted} />
      </div>
      <div style={{ display: "flex", gap: 10, marginBottom: 10, flexWrap: "wrap" }}>
        <KpiCard label="Núm. Créditos"    value={fmtN(k.num_creditos)}           unit="" />
        <KpiCard label="Saldo Promedio"   value={`$${fmtN(k.saldo_promedio_mxn)}`} unit="MXN" />
        <KpiCard label="Tasa Ponderada"   value={fmtTasa(k.tasa_ponderada)}      unit="" />
        <KpiCard label="Plazo Ponderado"  value={fmt(k.plazo_ponderado_meses, 0)} unit="meses" />
      </div>
      <div style={{ display: "flex", gap: 10, marginBottom: 14, flexWrap: "wrap" }}>
        <KpiCard label="YoY Nominal" value={fmtPct(k.delta_total_yoy)}      unit="" accentColor={k.delta_total_yoy > 0 ? COLOR.green : COLOR.red} />
        {k.delta_total_yoy_real != null && (
          <KpiCard label="YoY Real" value={fmtPct(k.delta_total_yoy_real)} unit="" accentColor={k.delta_total_yoy_real > 0 ? COLOR.green : COLOR.red} />
        )}
        <KpiCard label="Saldo MN" value={fmtB(k.saldo_mn_b)} unit="" accentColor={COLOR.blue} />
        <KpiCard label="Saldo ME" value={fmtB(k.saldo_me_b)} unit="" delta={k.pct_me} deltaLabel="% del total" />
        {k.tipo_cambio_fix && <KpiCard label="FIX Banxico" value={`$${fmt(k.tipo_cambio_fix, 2)}`} unit="MXN/USD" accentColor={COLOR.muted} />}
      </div>
      <InsightBox text={s?.resumen?.narrativa} />
    </div>
  );
}

function SeccionCalidad({ s }) {
  const cal = s?.calidad || {};
  const { por_etapa, por_cartera } = cal;
  const etapas = [
    { key: "etapa1", label: "Etapa 1 — Riesgo normal",           color: COLOR.green },
    { key: "etapa2", label: "Etapa 2 — Deterioro significativo", color: COLOR.gold  },
    { key: "etapa3", label: "Etapa 3 — Vencida",                 color: COLOR.red   },
  ];
  const maxSaldo = Math.max(...etapas.map(e => por_etapa?.[e.key]?.saldo_b || 0), 0.001);
  return (
    <div>
      <div style={{ display: "flex", gap: 10, marginBottom: 16, flexWrap: "wrap" }}>
        {etapas.map(e => {
          const d = por_etapa?.[e.key] || {};
          return <KpiCard key={e.key} label={e.label} value={fmtB(d.saldo_b)} unit=""
            delta={d.delta_mom} deltaLabel="MoM" accentColor={e.color} />;
        })}
      </div>
      {etapas.map(e => {
        const d = por_etapa?.[e.key] || {};
        return (
          <BarRow key={e.key} label={e.label} value={d.saldo_b || 0} maxValue={maxSaldo}
            color={e.color} rightLabel={`${fmt(d.pct, 1)}% del total`} />
        );
      })}
      {por_cartera?.length > 0 && (
        <div style={{ marginTop: 16 }}>
          <div style={{ fontSize: 13, color: COLOR.muted, fontFamily: "monospace", textTransform: "uppercase", letterSpacing: ".5px", marginBottom: 8 }}>
            Por tipo de cartera
          </div>
          {por_cartera.map((c, i) => (
            <div key={i} style={{ display: "flex", justifyContent: "space-between", padding: "6px 0", borderBottom: `1px solid ${COLOR.border}33`, fontSize: 14, fontFamily: "monospace" }}>
              <span style={{ color: COLOR.text }}>{c.cartera}</span>
              <span style={{ color: COLOR.muted }}>
                {fmtB(c.saldo_b)} ·{" "}
                <span style={{ color: c.imor > 3 ? COLOR.red : COLOR.gold, fontWeight: 700 }}>IMOR {fmtImor(c.imor)}</span>
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function SeccionRollRates({ s }) {
  // El backend ahora retorna la sección como "migracion_saldos"
  const rr  = s?.migracion_saldos || s?.roll_rates || {};
  const mat = rr.matriz_actual || {};
  const historico  = rr.historico || [];
  const narrativa  = rr.narrativa || "";

  const migraciones = [
    { label: "E1 → E2 (deterioro inicial)",  value: mat.roll_e1_e2_pct, color: COLOR.gold },
    { label: "E2 → E3 (deterioro confirmado)", value: mat.roll_e2_e3_pct, color: COLOR.red  },
    { label: "E3 → recuperación (cura)",      value: mat.cure_rate_pct,  color: COLOR.green },
  ];
  return (
    <div>
      <div style={{ display: "flex", gap: 10, marginBottom: 16, flexWrap: "wrap" }}>
        {migraciones.map((m, i) => (
          <KpiCard key={i} label={m.label}
            value={m.value != null ? `${fmt(m.value, 4)}%` : "—"}
            unit="" accentColor={m.color} />
        ))}
      </div>

      {/* Comentario analítico LLM */}
      <InsightBox text={narrativa} />

      {historico.length > 0 && (
        <>
          <div style={{ fontSize: 13, color: COLOR.muted, fontFamily: "monospace", textTransform: "uppercase", letterSpacing: ".5px", marginBottom: 8, marginTop: 16 }}>
            Histórico de migración (últimos 12 periodos)
          </div>
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14, fontFamily: "monospace" }}>
              <thead>
                <tr style={{ borderBottom: `1px solid ${COLOR.border}` }}>
                  {["Periodo","E1→E2 %","E2→E3 %","Cura %","E1 (mmdp)","E2 (mmdp)","E3 (mmdp)"].map(h => (
                    <th key={h} style={{ padding: "4px 8px", color: COLOR.muted, fontWeight: 600, textAlign: "right", whiteSpace: "nowrap" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {[...historico].reverse().slice(0, 12).map((row, i) => (
                  <tr key={i} style={{ borderBottom: `1px solid ${COLOR.border}33` }}>
                    <td style={{ padding: "4px 8px", color: COLOR.gold,  textAlign: "right" }}>{row.periodo}</td>
                    <td style={{ padding: "4px 8px", color: COLOR.gold,  textAlign: "right" }}>{row.roll_e1_e2 != null ? fmt(row.roll_e1_e2, 4) : "—"}</td>
                    <td style={{ padding: "4px 8px", color: COLOR.red,   textAlign: "right" }}>{row.roll_e2_e3 != null ? fmt(row.roll_e2_e3, 4) : "—"}</td>
                    <td style={{ padding: "4px 8px", color: COLOR.green, textAlign: "right" }}>{row.cure_rate  != null ? fmt(row.cure_rate,  4) : "—"}</td>
                    <td style={{ padding: "4px 8px", color: COLOR.muted, textAlign: "right" }}>{fmtB(row.e1_b)}</td>
                    <td style={{ padding: "4px 8px", color: COLOR.muted, textAlign: "right" }}>{fmtB(row.e2_b)}</td>
                    <td style={{ padding: "4px 8px", color: COLOR.muted, textAlign: "right" }}>{fmtB(row.e3_b)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
      <div style={{ marginTop: 8, fontSize: 12, color: COLOR.dim, fontFamily: "monospace" }}>
        * Tasas implícitas calculadas sobre variación de saldos agregados entre periodos consecutivos, no sobre cohortes individuales de crédito.
      </div>
    </div>
  );
}

function SeccionOriginacion({ s }) {
  const orig = s?.originacion || {};
  const k    = orig.kpis || {};
  const act  = orig.por_actividad   || [];
  const tam  = orig.por_tamano      || [];
  const inst = orig.por_institucion || [];

  const fmtSaldo   = n => n == null ? "—" : `${(n||0).toFixed(2)} mmdp`;
  const fmtDelta   = n => n == null ? "—" : `${n >= 0 ? "▲ +" : "▼ "}${fmt(n,1)}%`;
  const deltaColor = n => n == null ? COLOR.muted : n >= 0 ? COLOR.green : COLOR.red;

  const maxActSaldo  = Math.max(...act.map(a => a.saldo_b || 0), 0.001);
  const maxTamSaldo  = Math.max(...tam.map(t => t.saldo_b || 0), 0.001);
  const maxInstSaldo = Math.max(...inst.map(i => i.saldo_b || 0), 0.001);

  const SubH = ({ t }) => (
    <div style={{ fontSize: 13, color: COLOR.muted, fontFamily: "monospace", textTransform: "uppercase",
      letterSpacing: ".5px", marginBottom: 10, paddingBottom: 6, borderBottom: `1px solid ${COLOR.border}` }}>{t}</div>
  );

  return (
    <div>
      {/* KPIs principales — métrica saldo */}
      <div style={{ display: "flex", gap: 10, marginBottom: 6, flexWrap: "wrap" }}>
        <KpiCard label="Nuevas cuentas"   value={fmtN(k.nuevas_cuentas)}    unit=""
          delta={k.delta_cuentas_mom} deltaLabel="MoM" />
        <KpiCard label="Saldo originado"  value={fmtSaldo(k.saldo_b)}       unit=""
          delta={k.delta_saldo_mom} deltaLabel="MoM" />
        <KpiCard label="Saldo promedio"   value={`$${fmtN(Math.round(k.saldo_promedio_mxn||0))} MXN`} unit="" />
        <KpiCard label="Tasa ponderada"   value={k.tasa_ponderada != null ? `${fmt(k.tasa_ponderada,2)}%` : "—"} unit="" />
        <KpiCard label="Plazo ponderado"  value={k.plazo_ponderado != null ? `${fmt(k.plazo_ponderado,0)} meses` : "—"} unit="" />
      </div>

      {/* YoY + monto dispuesto informativo */}
      <div style={{ display: "flex", gap: 10, marginBottom: 20, flexWrap: "wrap" }}>
        <KpiCard label="Cuentas YoY"     value={fmtDelta(k.delta_cuentas_yoy)} unit=""
          accentColor={deltaColor(k.delta_cuentas_yoy)} />
        <KpiCard label="Saldo YoY"       value={fmtDelta(k.delta_saldo_yoy)}   unit=""
          accentColor={deltaColor(k.delta_saldo_yoy)} />
        <div style={{ background: COLOR.surface, border: `1px solid ${COLOR.border}`, borderRadius: 6,
          padding: "10px 14px", flex: 1, minWidth: 160 }}>
          <div style={{ fontSize: 13, color: COLOR.dim, fontFamily: "monospace", textTransform: "uppercase",
            letterSpacing: ".5px", marginBottom: 4 }}>Monto dispuesto (informativo)</div>
          <div style={{ fontSize: 16, fontWeight: 700, color: COLOR.muted, fontFamily: "monospace" }}>
            {fmtSaldo(k.monto_dispuesto_b)}
          </div>
          <div style={{ fontSize: 13, color: COLOR.dim, fontFamily: "monospace", marginTop: 2 }}>
            Monto desembolsado en el periodo
          </div>
        </div>
      </div>

      {/* Grid 3 columnas */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 20, marginBottom: 20 }}>

        {/* Por actividad económica */}
        <div>
          <SubH t="Top 10 — Actividad Económica" />
          {act.map((item, i) => (
            <div key={i} style={{ marginBottom: 8 }}>
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13, fontFamily: "monospace", marginBottom: 2 }}>
                <span style={{ color: COLOR.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: "58%" }}>{item.actividad}</span>
                <span style={{ color: COLOR.gold, fontWeight: 700 }}>{fmtSaldo(item.saldo_b)}</span>
              </div>
              <div style={{ height: 4, background: COLOR.border, borderRadius: 2, marginBottom: 1 }}>
                <div style={{ height: "100%", borderRadius: 2, background: COLOR.blue,
                  width: `${Math.min(100, (item.saldo_b / maxActSaldo) * 100)}%` }} />
              </div>
              <div style={{ fontSize: 12, color: COLOR.dim, fontFamily: "monospace" }}>
                {fmtN(item.nuevas_cuentas)} cuentas
                {item.tasa_ponderada > 0 && ` · tasa ${fmt(item.tasa_ponderada,2)}%`}
                {item.plazo_ponderado > 0 && ` · ${fmt(item.plazo_ponderado,0)} meses`}
                {" · disp. "}{fmtSaldo(item.monto_dispuesto_b)}
              </div>
            </div>
          ))}
        </div>

        {/* Por tamaño empresa */}
        <div>
          <SubH t="Por Tamaño de Empresa" />
          {tam.map((item, i) => (
            <div key={i} style={{ marginBottom: 10 }}>
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13, fontFamily: "monospace", marginBottom: 2 }}>
                <span style={{ color: COLOR.text }}>{item.segmento}</span>
                <span style={{ color: COLOR.gold, fontWeight: 700 }}>{item.pct_saldo?.toFixed(1)}%</span>
              </div>
              <div style={{ height: 4, background: COLOR.border, borderRadius: 2, marginBottom: 1 }}>
                <div style={{ height: "100%", borderRadius: 2, background: COLOR.blue,
                  width: `${Math.min(100, (item.saldo_b / maxTamSaldo) * 100)}%` }} />
              </div>
              <div style={{ fontSize: 12, color: COLOR.dim, fontFamily: "monospace" }}>
                {fmtSaldo(item.saldo_b)} · {fmtN(item.numero_acreditados)} acreditados · {fmtN(item.nuevas_cuentas)} créditos
                {item.tasa_ponderada > 0 && ` · tasa ${fmt(item.tasa_ponderada,2)}%`}
              </div>
            </div>
          ))}
        </div>

        {/* Por institución */}
        <div>
          <SubH t="Top 10 — Institución" />
          {inst.map((item, i) => (
            <div key={i} style={{ marginBottom: 8 }}>
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13, fontFamily: "monospace", marginBottom: 2 }}>
                <div>
                  <span style={{ color: COLOR.dim, marginRight: 5 }}>{i + 1}</span>
                  <span style={{ color: COLOR.text, fontWeight: 600 }}>{item.nombre_entidad}</span>
                </div>
                <span style={{ color: COLOR.gold, fontWeight: 700 }}>{fmtSaldo(item.saldo_b)}</span>
              </div>
              <div style={{ height: 4, background: COLOR.border, borderRadius: 2, marginBottom: 1 }}>
                <div style={{ height: "100%", borderRadius: 2, background: COLOR.blue,
                  width: `${Math.min(100, (item.saldo_b / maxInstSaldo) * 100)}%` }} />
              </div>
              <div style={{ fontSize: 12, color: COLOR.dim, fontFamily: "monospace" }}>
                {fmtN(item.nuevas_cuentas)} cuentas · disp. {fmtSaldo(item.monto_dispuesto_b)}
              </div>
            </div>
          ))}
        </div>
      </div>

      <InsightBox text={orig.narrativa} />
    </div>
  );
}

function SeccionSectorial({ s }) {
  const sec = s?.sectorial || {};
  // Compatibilidad con respuestas antiguas (secciones separadas)
  const actData  = sec.actividad || s?.actividad || {};
  const tamData  = sec.tamano    || s?.tamano    || [];
  const destData = sec.destino   || s?.destino   || {};
  const narrativa = sec.narrativa || "";

  const [vistaAct,  setVistaAct]  = useState("saldo");
  const [vistaDest, setVistaDest] = useState("saldo");

  const btnStyle = (active) => ({
    padding: "3px 10px", borderRadius: 4, fontSize: 13, fontFamily: "monospace",
    cursor: "pointer", border: `1px solid ${active ? COLOR.gold : COLOR.border}`,
    background: active ? `${COLOR.gold}18` : "none",
    color: active ? COLOR.gold : COLOR.muted,
  });

  const SubHeader = ({ title, sub }) => (
    <div style={{ marginBottom: 12, paddingBottom: 8, borderBottom: `1px solid ${COLOR.border}` }}>
      <span style={{ fontSize: 14, fontWeight: 700, color: COLOR.gold, fontFamily: "monospace", textTransform: "uppercase", letterSpacing: ".5px" }}>{title}</span>
      {sub && <span style={{ fontSize: 13, color: COLOR.dim, fontFamily: "monospace", marginLeft: 10 }}>{sub}</span>}
    </div>
  );

  // Actividad
  const actItems = vistaAct === "saldo" ? (actData.top_saldo || []) : (actData.top_imor || []);
  const actMax   = Math.max(...actItems.map(d => vistaAct === "saldo" ? (d.saldo_b||0) : (d.imor||0)), 0.001);

  // Tamaño
  const tamMax = Math.max(...tamData.map(d => d.saldo_b||0), 0.001);

  // Destino
  const destItems = vistaDest === "saldo" ? (destData.top_saldo||[]) : (destData.top_imor||[]);
  const destMax   = Math.max(...destItems.map(d => vistaDest === "saldo" ? (d.saldo_b||0) : (d.imor||0)), 0.001);

  return (
    <div>
      {/* Grid 3 columnas */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 20, marginBottom: 20 }}>

        {/* Actividad económica */}
        <div>
          <SubHeader title="Actividad Económica" sub={`${actData.total_actividades||0} sectores`} />
          <div style={{ display: "flex", gap: 6, marginBottom: 12 }}>
            {[["saldo","Por saldo"],["imor","Por IMOR"]].map(([v,l]) => (
              <button key={v} onClick={() => setVistaAct(v)} style={btnStyle(vistaAct===v)}>{l}</button>
            ))}
          </div>
          {actItems.map((item, i) => (
            <div key={i} style={{ marginBottom: 8 }}>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 2, fontSize: 13, fontFamily: "monospace" }}>
                <span style={{ color: COLOR.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: "60%" }}>{item.actividad}</span>
                <span style={{ color: item.imor > 3 ? COLOR.red : COLOR.gold, fontWeight: 700 }}>{fmtImor(item.imor)}</span>
              </div>
              <div style={{ height: 4, background: COLOR.border, borderRadius: 2 }}>
                <div style={{ height:"100%", borderRadius:2, background: vistaAct==="imor" ? (item.imor>3?COLOR.red:COLOR.gold) : COLOR.blue,
                  width:`${Math.min(100,((vistaAct==="saldo"?item.saldo_b:item.imor)/actMax)*100)}%` }} />
              </div>
              {vistaAct==="saldo" && <div style={{ fontSize:9, color:COLOR.dim, fontFamily:"monospace", marginTop:1 }}>{fmtB(item.saldo_b)}</div>}
            </div>
          ))}
        </div>

        {/* Tamaño de empresa */}
        <div>
          <SubHeader title="Tamaño de Empresa" />
          {tamData.map((item, i) => (
            <div key={i} style={{ marginBottom: 10 }}>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 2, fontSize: 13, fontFamily: "monospace" }}>
                <span style={{ color: COLOR.text }}>{item.segmento}</span>
                <span style={{ color: item.imor > 3 ? COLOR.red : COLOR.gold, fontWeight: 700 }}>{fmtImor(item.imor)}</span>
              </div>
              <div style={{ height: 4, background: COLOR.border, borderRadius: 2, marginBottom: 2 }}>
                <div style={{ height:"100%", borderRadius:2, background: COLOR.blue,
                  width:`${Math.min(100,((item.saldo_b||0)/tamMax)*100)}%` }} />
              </div>
              <div style={{ fontSize:9, color:COLOR.dim, fontFamily:"monospace" }}>
                {fmtB(item.saldo_b)} · {fmtN(item.num_creditos)} cred.
                {item.tasa_ponderada > 0 && ` · Tasa ${fmtTasa(item.tasa_ponderada)}`}
              </div>
            </div>
          ))}
        </div>

        {/* Destino de crédito */}
        <div>
          <SubHeader title="Destino de Crédito" />
          <div style={{ display: "flex", gap: 6, marginBottom: 12 }}>
            {[["saldo","Por saldo"],["imor","Por IMOR"]].map(([v,l]) => (
              <button key={v} onClick={() => setVistaDest(v)} style={btnStyle(vistaDest===v)}>{l}</button>
            ))}
          </div>
          {destItems.map((item, i) => (
            <div key={i} style={{ marginBottom: 8 }}>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 2, fontSize: 13, fontFamily: "monospace" }}>
                <span style={{ color: COLOR.text, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap", maxWidth:"65%" }}>{item.destino}</span>
                <span style={{ color: item.imor > 3 ? COLOR.red : COLOR.gold, fontWeight: 700 }}>{fmtImor(item.imor)}</span>
              </div>
              <div style={{ height: 4, background: COLOR.border, borderRadius: 2 }}>
                <div style={{ height:"100%", borderRadius:2,
                  background: vistaDest==="imor"?(item.imor>3?COLOR.red:COLOR.gold):COLOR.blue,
                  width:`${Math.min(100,((vistaDest==="saldo"?item.saldo_b:item.imor)/destMax)*100)}%` }} />
              </div>
              {vistaDest==="saldo" && <div style={{ fontSize:9, color:COLOR.dim, fontFamily:"monospace", marginTop:1 }}>{fmtB(item.saldo_b)}</div>}
            </div>
          ))}
        </div>
      </div>

      {/* Comentario analítico integrado */}
      <InsightBox text={narrativa} />
    </div>
  );
}

function SeccionTerritorio({ s }) {
  const [metrica,   setMetrica]   = useState("saldo_b");
  const [subVista,  setSubVista]  = useState("estado"); // "estado" | "municipio"

  const data = (s?.territorio || []).map(d => ({ ...d, nombre_estado: d.estado }));

  // municipio: el backend puede retornar array o {datos, advertencia}
  const munRaw     = s?.territorio_municipio;
  const munDatos   = Array.isArray(munRaw) ? munRaw : (munRaw?.datos || []);
  const munAdv     = munRaw?.advertencia || null;

  const metricas = [
    { key: "saldo_b",   label: "Saldo total"    },
    { key: "vencido_b", label: "Cartera vencida" },
    { key: "imor",      label: "IMOR"            },
  ];
  const tooltipFn = useCallback((d, nombre) => `
    <div style="font-family:monospace;padding:6px 8px;min-width:180px;background:#0d1520;border:1px solid #1a2535;border-radius:4px">
      <div style="font-size:12px;font-weight:700;color:#dde4ee;margin-bottom:5px">${nombre}</div>
      <div style="font-size:10px;color:#c8a96e;margin-bottom:4px">Saldo: ${fmtB(d?.saldo_b)}</div>
      <div style="font-size:10px;color:#8aa4b8;line-height:1.7">
        Vencido: ${fmtB(d?.vencido_b)}<br/>
        IMOR: ${fmtImor(d?.imor)}<br/>
        Créditos: ${fmtN(d?.num_creditos)}
      </div>
    </div>
  `, []);

  const btnStyle = (active) => ({
    padding: "3px 12px", borderRadius: 4, fontSize: 13, fontFamily: "monospace",
    cursor: "pointer", border: `1px solid ${active ? COLOR.gold : COLOR.border}`,
    background: active ? `${COLOR.gold}18` : "none",
    color: active ? COLOR.gold : COLOR.muted,
  });

  return (
    <div>
      {/* Sub-tab Estado / Municipio */}
      <div style={{ display: "flex", gap: 6, marginBottom: 14 }}>
        <button onClick={() => setSubVista("estado")}   style={btnStyle(subVista === "estado")}>Estado</button>
        <button onClick={() => setSubVista("municipio")} style={btnStyle(subVista === "municipio")}>Municipio</button>
      </div>

      {/* ── Vista Estado — layout igual que captación ── */}
      {subVista === "estado" && (
        <div>
          {/* Selector métrica */}
          <div style={{ display: "flex", gap: 6, marginBottom: 10 }}>
            {metricas.map(m => (
              <button key={m.key} onClick={() => setMetrica(m.key)} style={btnStyle(metrica === m.key)}>
                {m.label}
              </button>
            ))}
          </div>

          {/* Layout mapa + ranking */}
          <div style={{ display: "flex", gap: 16, alignItems: "flex-start" }}>

            {/* Mapa */}
            <div style={{ flex: "0 0 58%" }}>
              <div style={{ fontSize: 13, color: COLOR.muted, fontFamily: "monospace", marginBottom: 6 }}>
                Mapa por Estado — {metricas.find(m => m.key === metrica)?.label}
              </div>
              <div style={{ height: 360, borderRadius: 6, overflow: "hidden", marginBottom: 6 }}>
                <MapaEstados data={data} metrica={metrica} tooltipFn={tooltipFn} />
              </div>
              <MapaLeyenda />
            </div>

            {/* Ranking */}
            <div style={{ flex: 1, overflowY: "auto", maxHeight: 420 }}>
              <div style={{ fontSize: 13, color: COLOR.muted, fontFamily: "monospace", textTransform: "uppercase", letterSpacing: ".5px", marginBottom: 10 }}>
                Ranking — {metricas.find(m => m.key === metrica)?.label}
              </div>
              {(() => {
                const sorted = [...(s?.territorio || [])]
                  .sort((a,b) => (b[metrica]||0) - (a[metrica]||0));
                const maxVal = sorted[0]?.[metrica] || 1;
                return sorted.map((item, i) => (
                  <div key={i} style={{ marginBottom: 8 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13, fontFamily: "monospace", marginBottom: 2 }}>
                      <div>
                        <span style={{ color: COLOR.dim, marginRight: 6 }}>{i+1}</span>
                        <span style={{ color: COLOR.text, fontWeight: 600, textTransform: "uppercase" }}>{item.estado}</span>
                      </div>
                      <span style={{
                        color: metrica === "imor" ? (item.imor > 3 ? COLOR.red : COLOR.gold) : COLOR.gold,
                        fontWeight: 700
                      }}>
                        {metrica === "saldo_b"   ? fmtB(item.saldo_b)
                         : metrica === "vencido_b" ? fmtB(item.vencido_b)
                         : fmtImor(item.imor)}
                      </span>
                    </div>
                    <div style={{ height: 4, background: COLOR.border, borderRadius: 2 }}>
                      <div style={{
                        height: "100%", borderRadius: 2,
                        width: `${Math.min(100, ((item[metrica]||0) / maxVal) * 100)}%`,
                        background: metrica === "imor"
                          ? (item.imor > 3 ? COLOR.red : COLOR.gold) : COLOR.blue,
                      }} />
                    </div>
                  </div>
                ));
              })()}
            </div>
          </div>
        </div>
      )}

      {/* ── Vista Municipio ── */}
      {subVista === "municipio" && (
        <div>
          {munAdv && (
            <div style={{ fontSize: 13, color: COLOR.muted, fontFamily: "monospace", background: `${COLOR.gold}0d`, border: `1px solid ${COLOR.border}`, borderRadius: 4, padding: "6px 10px", marginBottom: 10 }}>
              ⚠ {munAdv}
            </div>
          )}
          {munDatos.length === 0 ? (
            <div style={{ fontSize: 15, color: COLOR.muted, fontFamily: "monospace", padding: "20px 0" }}>
              Sin datos de municipio disponibles.
            </div>
          ) : (
            <>
              {/* Selector métrica */}
              <div style={{ display: "flex", gap: 6, marginBottom: 10 }}>
                {metricas.map(m => (
                  <button key={m.key} onClick={() => setMetrica(m.key)} style={btnStyle(metrica === m.key)}>{m.label}</button>
                ))}
              </div>

              {/* Layout: mapa izquierda + ranking derecha (igual que captación) */}
              <div style={{ display: "flex", gap: 16, alignItems: "flex-start" }}>

                {/* Mapa */}
                <div style={{ flex: "0 0 62%" }}>
                  <div style={{ fontSize: 13, color: COLOR.muted, fontFamily: "monospace", marginBottom: 6 }}>
                    Mapa Municipal — {metricas.find(m => m.key === metrica)?.label}
                    <span style={{ color: COLOR.dim, marginLeft: 8 }}>
                      ({munDatos.filter(d => d.cvegeo).length} municipios con dato / {munDatos.length} total)
                    </span>
                  </div>
                  <MapaComMunicipio municipioData={munDatos} metrica={metrica} />
                  <MapaLeyenda />
                  <div style={{ fontSize: 12, color: COLOR.dim, fontFamily: "monospace", marginTop: 4 }}>
                    Los municipios sin color no reportan cartera comercial o están agrupados en "Otros".
                  </div>
                </div>

                {/* Ranking */}
                <div style={{ flex: 1, overflowY: "auto", maxHeight: 440 }}>
                  <div style={{ fontSize: 13, color: COLOR.muted, fontFamily: "monospace", textTransform: "uppercase", letterSpacing: ".5px", marginBottom: 8 }}>
                    Ranking — {metricas.find(m => m.key === metrica)?.label} (Top 50)
                  </div>
                  {(() => {
                    const sorted = [...munDatos]
                      .filter(d => d.municipio !== "Otros" && (d.saldo_b || 0) >= 0.1) // ≥100 MDP
                      .sort((a,b) => (b[metrica]||0) - (a[metrica]||0))
                      .slice(0, 50);
                    const maxVal = sorted[0]?.[metrica] || 1;
                    return sorted.map((m, i) => (
                      <div key={i} style={{ marginBottom: 8 }}>
                        <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13, fontFamily: "monospace", marginBottom: 2 }}>
                          <div>
                            <span style={{ color: COLOR.dim, marginRight: 6 }}>{i+1}</span>
                            <span style={{ color: COLOR.text, fontWeight: 600, textTransform: "uppercase" }}>{m.municipio}</span>
                          </div>
                          <span style={{
                            color: metrica === "imor" ? (m.imor > 3 ? COLOR.red : COLOR.gold) : COLOR.gold,
                            fontWeight: 700, fontSize: 13
                          }}>
                            {metrica === "saldo_b" ? fmtB(m.saldo_b)
                             : metrica === "vencido_b" ? fmtB(m.vencido_b)
                             : fmtImor(m.imor)}
                          </span>
                        </div>
                        <div style={{ fontSize: 12, color: COLOR.dim, fontFamily: "monospace", marginBottom: 3 }}>{m.estado}</div>
                        <div style={{ height: 4, background: COLOR.border, borderRadius: 2 }}>
                          <div style={{
                            height: "100%", borderRadius: 2,
                            width: `${Math.min(100, ((m[metrica]||0) / maxVal) * 100)}%`,
                            background: metrica === "imor"
                              ? (m.imor > 3 ? COLOR.red : COLOR.gold)
                              : COLOR.blue,
                          }} />
                        </div>
                      </div>
                    ));
                  })()}
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════
// MAPA MUNICIPIOS (Leaflet canvas — igual que captación)
// ══════════════════════════════════════════════════════════════════════
const GEOJSON_MUN_URL = "/municipios_mx.geojson";
let _cacheMunCom = null, _promMunCom = null;
function fetchGeoMunCom() {
  if (_cacheMunCom) return Promise.resolve(_cacheMunCom);
  if (_promMunCom)  return _promMunCom;
  _promMunCom = fetch(GEOJSON_MUN_URL).then(r => r.json())
    .then(d => { _cacheMunCom = d; return d; });
  return _promMunCom;
}

function MapaComMunicipio({ municipioData, metrica }) {
  const mapRef     = useRef(null);
  const leafletRef = useRef(null);
  const geoRef     = useRef(null);
  const [geojson, setGeojson] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    fetchGeoMunCom()
      .then(d => { setGeojson(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  const dataIdx = React.useMemo(() => {
    if (!municipioData) return {};
    return Object.fromEntries(municipioData.map(d => [d.cvegeo, d]));
  }, [municipioData]);

  const sortedVals = React.useMemo(() => {
    if (!municipioData?.length) return [];
    return municipioData.map(d => d[metrica] || 0).filter(v => v > 0).sort((a,b) => a-b);
  }, [municipioData, metrica]);

  const colorScale = useCallback((val) => {
    if (!val || val <= 0 || !sortedVals.length) return "#1a2535";
    let lo = 0, hi = sortedVals.length - 1;
    while (lo < hi) { const mid = (lo+hi)>>1; if (sortedVals[mid] < val) lo=mid+1; else hi=mid; }
    const pct = sortedVals.length > 1 ? lo / (sortedVals.length - 1) : 1;
    if (pct < 0.08) return "#1e3a8a";
    if (pct < 0.20) return "#0369a1";
    if (pct < 0.34) return "#0891b2";
    if (pct < 0.50) return "#0d9488";
    if (pct < 0.65) return "#16a34a";
    if (pct < 0.80) return "#ca8a04";
    if (pct < 0.92) return "#ea580c";
    return "#dc2626";
  }, [sortedVals]);

  // Inicializar Leaflet — se ejecuta cuando el div esté en el DOM
  // IMPORTANTE: el div siempre se renderiza para que mapRef esté disponible
  useEffect(() => {
    if (!mapRef.current || !window.L) return;
    if (leafletRef.current) { leafletRef.current.invalidateSize(); return; }
    leafletRef.current = window.L.map(mapRef.current, {
      center: [23.8, -102.3], zoom: 5, zoomControl: true,
      attributionControl: false, scrollWheelZoom: true,
      preferCanvas: true, renderer: window.L.canvas({ padding: 0.5 }),
    });
    setTimeout(() => leafletRef.current?.invalidateSize(), 50);
    setTimeout(() => leafletRef.current?.invalidateSize(), 300);
  }, [loading]); // re-ejecutar cuando loading cambia (div ya en DOM)

  useEffect(() => {
    const L = window.L;
    if (!L || !leafletRef.current || !geojson || !municipioData) return;
    if (geoRef.current) { geoRef.current.remove(); geoRef.current = null; }
    geoRef.current = L.geoJSON(geojson, {
      style: (feature) => {
        const cv = feature.properties?.CVEGEO || "";
        const d  = dataIdx[cv];
        return {
          fillColor:   d ? colorScale(d[metrica] || 0) : "#0d1520",
          fillOpacity: d ? 0.82 : 0.3,
          color:       "#0d1520", weight: 0.4,
        };
      },
      onEachFeature: (feature, layer) => {
        const cv = feature.properties?.CVEGEO || "";
        const d  = dataIdx[cv];
        if (d) {
          layer.bindTooltip(
            `<b style="font-family:monospace">${d.municipio}, ${d.estado}</b><br/>` +
            `Saldo: ${d.saldo_b?.toFixed(2)} mmdp<br/>` +
            `IMOR: ${d.imor?.toFixed(2)}%<br/>` +
            `Créditos: ${(d.num_creditos||0).toLocaleString("es-MX")}`,
            { sticky: true }
          );
        }
      },
    }).addTo(leafletRef.current);
  }, [geojson, dataIdx, metrica, colorScale, municipioData]);

  // Fondo oscuro para el contenedor Leaflet (override del default #ddd)
  const mapContainerStyle = {
    position: "relative", height: 380, borderRadius: 6, overflow: "hidden",
    background: COLOR.bg,
  };

  return (
    <div style={mapContainerStyle}>
      {/* CSS override para fondo Leaflet */}
      <style>{`.leaflet-container { background: ${COLOR.bg} !important; }`}</style>
      <div ref={mapRef} style={{ height: "100%", width: "100%" }} />
      {loading && (
        <div style={{
          position: "absolute", inset: 0, display: "flex",
          alignItems: "center", justifyContent: "center",
          background: COLOR.bg, color: COLOR.muted, fontSize: 14, fontFamily: "monospace",
        }}>
          Cargando mapa de municipios…
        </div>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════
// GRÁFICA PRONÓSTICO DUAL EJE (Chart.js — saldo izq / IMOR der)
// ══════════════════════════════════════════════════════════════════════
function ForecastChartCom({ saldoHist, saldoProy, imorHist, imorProy }) {
  const canvasRef = useRef(null);
  const chartRef  = useRef(null);

  useEffect(() => {
    if (!canvasRef.current || !window.Chart) return;

    // Unificar periodos
    const allPer = [...new Set([
      ...(saldoHist || []).map(d => String(d.periodo)),
      ...(saldoProy || []).map(d => String(d.periodo)),
    ])].sort();

    const histIdx  = Object.fromEntries((saldoHist || []).map(d => [String(d.periodo), d.saldo_b]));
    const proyIdx  = Object.fromEntries((saldoProy || []).map(d => [String(d.periodo), d.valor / 1e9]));
    const lowerIdx = Object.fromEntries((saldoProy || []).map(d => [String(d.periodo), d.lower != null ? d.lower / 1e9 : null]));
    const upperIdx = Object.fromEntries((saldoProy || []).map(d => [String(d.periodo), d.upper != null ? d.upper / 1e9 : null]));

    const imorHistIdx = Object.fromEntries((imorHist || []).map(d => [String(d.periodo), d.imor]));
    const imorProyIdx = Object.fromEntries((imorProy || []).map(d => [String(d.periodo), d.valor]));

    const labels    = allPer.map(p => `${p.slice(0,4)}-${p.slice(4)}`);
    const saldoH    = allPer.map(p => histIdx[p]  ?? null);
    const saldoP    = allPer.map(p => proyIdx[p]  ?? null);
    const saldoLow  = allPer.map(p => lowerIdx[p] ?? null);
    const saldoHigh = allPer.map(p => upperIdx[p] ?? null);
    const imorH     = allPer.map(p => imorHistIdx[p] ?? null);
    const imorP     = allPer.map(p => imorProyIdx[p] ?? null);

    if (chartRef.current) chartRef.current.destroy();

    chartRef.current = new window.Chart(canvasRef.current, {
      type: "line",
      data: {
        labels,
        datasets: [
          { label: "Saldo histórico (mmdp)", data: saldoH, borderColor: COLOR.blue,
            backgroundColor:"transparent", borderWidth:2, pointRadius:2, tension:0.3, yAxisID:"y" },
          { label: "Saldo proyectado (mmdp)", data: saldoP, borderColor: COLOR.green,
            backgroundColor:"rgba(34,197,94,0.08)", borderWidth:2, borderDash:[5,3],
            pointRadius:3, tension:0.3, yAxisID:"y" },
          { label: "IC sup saldo", data: saldoHigh, borderColor:"rgba(34,197,94,0.25)",
            backgroundColor:"rgba(34,197,94,0.06)", borderWidth:1, pointRadius:0,
            fill:"-1", tension:0.3, yAxisID:"y" },
          { label: "IC inf saldo", data: saldoLow, borderColor:"rgba(34,197,94,0.25)",
            backgroundColor:"transparent", borderWidth:1, pointRadius:0,
            fill:false, tension:0.3, yAxisID:"y" },
          { label: "IMOR histórico (%)", data: imorH, borderColor: COLOR.gold,
            backgroundColor:"transparent", borderWidth:2, pointRadius:2,
            tension:0.3, yAxisID:"y2", borderDash:[] },
          { label: "IMOR proyectado (%)", data: imorP, borderColor: COLOR.red,
            backgroundColor:"rgba(248,113,113,0.08)", borderWidth:2, borderDash:[5,3],
            pointRadius:3, tension:0.3, yAxisID:"y2" },
        ],
      },
      options: {
        responsive:true, maintainAspectRatio:false,
        plugins: {
          legend: { position:"top", labels:{ font:{size:10,family:"monospace"}, boxWidth:12, color:"#8aa4b8", filter: item => !item.text.startsWith("IC") } },
          tooltip: { mode:"index", intersect:false },
        },
        scales: {
          x: { ticks:{ font:{size:9,family:"monospace"}, maxRotation:45, color:"#8aa4b8" }, grid:{ color:"#1a2535" } },
          y: {
            position:"left", title:{ display:true, text:"mmdp", font:{size:9,family:"monospace"}, color:"#60a5fa" },
            ticks:{ font:{size:9,family:"monospace"}, color:"#60a5fa" }, grid:{ color:"#1a2535" },
          },
          y2: {
            position:"right", title:{ display:true, text:"IMOR %", font:{size:9,family:"monospace"}, color:"#c8a96e" },
            ticks:{ font:{size:9,family:"monospace"}, color:"#c8a96e" }, grid:{ drawOnChartArea:false },
          },
        },
        interaction:{ mode:"nearest", axis:"x" },
      },
    });
  }, [saldoHist, saldoProy, imorHist, imorProy]);

  return (
    <div style={{ background: COLOR.card, borderRadius:6, padding:"8px 4px", border:`1px solid ${COLOR.border}`, marginBottom:16 }}>
      <canvas ref={canvasRef} style={{ height:240 }} />
    </div>
  );
}

function SeccionPronostico({ s }) {
  const fc = s?.forecast || {};
  if (!fc.cubierto) {
    return (
      <div style={{ fontSize: 15, color: COLOR.muted, fontFamily: "monospace", padding: "20px 0" }}>
        {fc.advertencia || "Modelos ARIMA no disponibles para esta selección."}
      </div>
    );
  }

  const saldoData = fc.saldo || {};
  const imorData  = fc.imor  || {};
  const proy_s    = saldoData.proyecciones || [];
  const proy_i    = imorData.proyecciones  || [];
  const hist      = saldoData.historico    || [];
  const excluidas = imorData.excluidas     || [];

  const PanelForecast = ({ titulo, proyecciones, unidad, decimales, insight, advertencia }) => (
    <div style={{ background: COLOR.surface, border: `1px solid ${COLOR.border}`, borderRadius: 6, padding: 16 }}>
      <div style={{ fontSize: 13, color: COLOR.muted, fontFamily: "monospace", textTransform: "uppercase", letterSpacing: ".5px", marginBottom: 12 }}>
        {titulo}
      </div>
      {advertencia && (
        <div style={{ fontSize: 13, color: COLOR.gold, fontFamily: "monospace", background: `${COLOR.gold}0d`, border: `1px solid ${COLOR.border}`, borderRadius: 4, padding: "5px 8px", marginBottom: 10 }}>
          ⚠ {advertencia}
        </div>
      )}
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14, fontFamily: "monospace", marginBottom: 12 }}>
        <thead>
          <tr style={{ borderBottom: `1px solid ${COLOR.border}` }}>
            {["Periodo","Proyección","IC Bajo (90%)","IC Alto (90%)"].map(h => (
              <th key={h} style={{ padding: "4px 8px", color: COLOR.muted, fontWeight: 600, textAlign: "right" }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {proyecciones.map((p, i) => (
            <tr key={i} style={{ borderBottom: `1px solid ${COLOR.border}33` }}>
              <td style={{ padding: "4px 8px", color: COLOR.gold, textAlign: "right" }}>{p.periodo}</td>
              <td style={{ padding: "4px 8px", color: COLOR.text, textAlign: "right", fontWeight: 600 }}>
                {unidad === "mmdp" ? `${(p.valor / 1e9).toFixed(decimales)} mmdp` : `${fmt(p.valor, decimales)}%`}
              </td>
              <td style={{ padding: "4px 8px", color: COLOR.muted, textAlign: "right" }}>
                {p.lower != null ? (unidad === "mmdp" ? `${(p.lower / 1e9).toFixed(decimales)}` : `${fmt(p.lower, decimales)}%`) : "—"}
              </td>
              <td style={{ padding: "4px 8px", color: COLOR.muted, textAlign: "right" }}>
                {p.upper != null ? (unidad === "mmdp" ? `${(p.upper / 1e9).toFixed(decimales)}` : `${fmt(p.upper, decimales)}%`) : "—"}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <InsightBox text={insight} />
    </div>
  );

  return (
    <div>
      {/* Gráfica combinada eje doble */}
      <ForecastChartCom
        saldoHist={hist}
        saldoProy={proy_s}
        imorHist={hist}
        imorProy={proy_i}
      />

      {/* Paneles de tabla + narrativa */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 12 }}>
        <PanelForecast
          titulo="Saldo Total — Proyección"
          proyecciones={proy_s}
          unidad="mmdp"
          decimales={2}
          insight={saldoData.insight}
        />
        <PanelForecast
          titulo="IMOR — Proyección"
          proyecciones={proy_i}
          unidad="%"
          decimales={4}
          insight={imorData.insight}
          advertencia={excluidas.length > 0 ? `Excluidas por modelo no confiable: ${excluidas.join(", ")}` : null}
        />
      </div>
      <div style={{ fontSize: 12, color: COLOR.dim, fontFamily: "monospace" }}>
        Modelos ARIMA_PLUS entrenados en BigQuery ML · {fc.entidades_modelo?.length || 0} instituciones
        {fc.cobertura_pct != null ? ` · Cobertura estimada: ${fc.cobertura_pct}% del sistema` : ""}
        {" · Horizonte: "}{fc.horizonte} meses · Intervalo de confianza 90%
      </div>
    </div>
  );
}

const HHI_DESCRIPCION = `El Índice Herfindahl-Hirschman (HHI) mide la concentración de mercado sumando el cuadrado de las participaciones de cada institución. Escala: 0–10,000. Valores < 1,500 indican mercado competitivo; entre 1,500 y 2,500, concentración moderada; > 2,500, alta concentración. Un HHI de 10,000 significa que un solo participante controla el 100% del segmento.`;

const ANOMALIAS_DESCRIPCION = `Detección de deterioro acelerado de cartera por z-score del delta mensual de IMOR. Para cada par (institución, actividad económica) con saldo ≥ 500 MDP, se calcula la media y desviación estándar histórica del cambio mensual en IMOR. Se reportan casos donde el cambio actual supera 2 desviaciones estándar respecto al patrón histórico y el delta absoluto es ≥ 0.5 puntos porcentuales. Z > 0 indica deterioro; Z < 0 indica mejora.`;

function SeccionRiesgo({ s }) {
  const conc = s?.concentracion || {};
  const anom = s?.anomalias || {};
  return (
    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
      <div>
        <div style={{ fontSize: 13, color: COLOR.muted, fontFamily: "monospace", textTransform: "uppercase", letterSpacing: ".5px", marginBottom: 8 }}>Concentración HHI</div>
        <div style={{ fontSize: 13, color: COLOR.dim, fontFamily: "monospace", lineHeight: 1.6, marginBottom: 10, background: `${COLOR.surface}`, border: `1px solid ${COLOR.border}`, borderRadius: 4, padding: "6px 10px" }}>
          {HHI_DESCRIPCION}
        </div>
        {conc.insight && <div style={{ fontSize: 14, color: COLOR.muted, lineHeight: 1.6, marginBottom: 10 }}>{conc.insight}</div>}
        {(conc.filas || []).slice(0, 5).map((f, i) => (
          <div key={i} style={{ display: "flex", justifyContent: "space-between", padding: "5px 0", borderBottom: `1px solid ${COLOR.border}33`, fontSize: 14, fontFamily: "monospace" }}>
            <span style={{ color: COLOR.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: "60%" }}>{f.descripcion_estado}</span>
            <span style={{ color: f.categoria_hhi === "Alta" ? COLOR.red : f.categoria_hhi === "Moderada" ? COLOR.gold : COLOR.green, fontWeight: 700 }}>HHI {Math.round(f.hhi_actual)}</span>
          </div>
        ))}
        {!conc.filas?.length && <div style={{ fontSize: 14, color: COLOR.dim, fontFamily: "monospace" }}>Sin datos de concentración</div>}
      </div>
      <div>
        <div style={{ fontSize: 13, color: COLOR.muted, fontFamily: "monospace", textTransform: "uppercase", letterSpacing: ".5px", marginBottom: 8 }}>Anomalías Estadísticas</div>
        <div style={{ fontSize: 13, color: COLOR.dim, fontFamily: "monospace", lineHeight: 1.6, marginBottom: 10, background: `${COLOR.surface}`, border: `1px solid ${COLOR.border}`, borderRadius: 4, padding: "6px 10px" }}>
          {ANOMALIAS_DESCRIPCION}
        </div>
        {(anom.filas || []).slice(0, 5).map((a, i) => (
          <div key={i} style={{ padding: "6px 0", borderBottom: `1px solid ${COLOR.border}33` }}>
            <div style={{ fontSize: 14, color: COLOR.text, fontFamily: "monospace", marginBottom: 2 }}>
              {a.nombre_entidad}
            </div>
            <div style={{ fontSize: 13, color: COLOR.dim, fontFamily: "monospace", marginBottom: 2 }}>
              {a.actividad_economica || a.descripcion_estado}
            </div>
            <div style={{ fontSize: 13, color: COLOR.muted, fontFamily: "monospace" }}>
              IMOR {fmtImor(a.imor)} · Δ {a.delta_imor_pp != null ? `${a.delta_imor_pp > 0 ? "+" : ""}${fmt(a.delta_imor_pp, 2)} pp` : "—"}
              {" "}· z={fmt(a.z_score, 1)} · <span style={{ color: a.z_score > 2.5 ? COLOR.red : a.z_score > 2 ? COLOR.gold : COLOR.green, fontWeight: 700 }}>{a.tipo_anomalia}</span>
            </div>
          </div>
        ))}
        {!anom.filas?.length && <div style={{ fontSize: 14, color: COLOR.dim, fontFamily: "monospace" }}>Sin anomalías de IMOR relevantes en el periodo.</div>}
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════
// COMPONENTE PRINCIPAL
// ══════════════════════════════════════════════════════════════════════
export default function ReporteComercializacion({ user }) {
  const [modo,          setModo]          = useState("sectorial");
  const [entidad,       setEntidad]       = useState("");
  const [periodo,       setPeriodo]       = useState(PERIODOS[0]);
  const [loading,       setLoading]       = useState(false);
  const [reporte,       setReporte]       = useState(null);
  const [error,         setError]         = useState(null);
  const [seccionActiva, setSeccionActiva] = useState("resumen");
  const [exportando,    setExportando]    = useState(false);

  const generarReporte = async () => {
    if (modo === "institucional" && !entidad) { setError("Selecciona una institución."); return; }
    setLoading(true); setError(null); setReporte(null);
    try {
      const body = {
        modo, periodo: parseInt(periodo), usuario: user?.email || "",
        ...(modo === "institucional" && entidad ? { nombre_entidad: entidad } : {}),
      };
      const res = await fetch(`${API_URL}/reporte/comercializacion`, {
        method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body),
      });
      if (!res.ok) { const e = await res.json().catch(()=>({})); throw new Error(e.detail || `Error ${res.status}`); }
      const data = await res.json();
      setReporte(data);
      setSeccionActiva("resumen");
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  };

  const exportarHTML = () => {
    if (!reporte) return;
    setExportando(true);
    try {
      const html = generarHTMLExport(reporte);
      const blob = new Blob([html], { type: "text/html;charset=utf-8" });
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement("a");
      a.href     = url;
      a.download = `reporte_comercial_${reporte.periodo}_${(reporte.nombre_entidad||"sectorial").replace(/\s+/g,"_")}.html`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) { console.error("[EXPORT]", e); }
    finally { setExportando(false); }
  };

  const s = reporte?.secciones;
  const numAnomalias = s?.anomalias?.filas?.length || 0;

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%", background: COLOR.bg, fontFamily: "monospace", color: COLOR.text }}>

      {/* ── TOP BAR ── */}
      <div style={{ background: COLOR.surface, borderBottom: `1px solid ${COLOR.border}`, padding: "0 18px", display: "flex", alignItems: "center", gap: 12, flexShrink: 0, height: 44 }}>
        <div style={{ fontWeight: 700, fontSize: 16, letterSpacing: "0.05em", color: COLOR.gold, fontFamily: "monospace" }}>◈ CARTERA COMERCIAL</div>
        <div style={{ width: 1, height: 16, background: COLOR.border }} />
        <div style={{ fontSize: 14, color: COLOR.muted, flex: 1 }}>Crédito empresarial IFRS 9 — Banca Múltiple</div>

        {/* Modo */}
        <div style={{ display: "flex", background: COLOR.card, border: `1px solid ${COLOR.border}`, borderRadius: 6, padding: 2, gap: 2 }}>
          {["sectorial","institucional"].map(m => (
            <button key={m} onClick={() => { setModo(m); setReporte(null); }} style={{
              padding: "3px 11px", borderRadius: 4, fontSize: 13, fontWeight: 600,
              border: "none", cursor: "pointer", fontFamily: "monospace", letterSpacing: "0.04em",
              background: modo === m ? `${COLOR.gold}22` : "transparent",
              color: modo === m ? COLOR.gold : COLOR.muted,
            }}>{m === "sectorial" ? "◧ Sectorial" : "◨ Institucional"}</button>
          ))}
        </div>

        {/* Periodo */}
        <select value={periodo} onChange={e => setPeriodo(e.target.value)} style={{ background: COLOR.card, border: `1px solid ${COLOR.border}`, color: COLOR.text, borderRadius: 5, padding: "3px 8px", fontSize: 13, fontFamily: "monospace" }}>
          {PERIODOS.map(p => <option key={p} value={p}>{`${p.slice(0,4)}-${p.slice(4)}`}</option>)}
        </select>

        {/* Institución */}
        {modo === "institucional" && (
          <select value={entidad} onChange={e => setEntidad(e.target.value)} style={{ background: COLOR.card, border: `1px solid ${COLOR.border}`, color: COLOR.text, borderRadius: 5, padding: "3px 8px", fontSize: 13, fontFamily: "monospace", minWidth: 150 }}>
            <option value="">— Selecciona banco —</option>
            {INSTITUCIONES.map(i => <option key={i} value={i}>{i}</option>)}
          </select>
        )}

        <button onClick={generarReporte} disabled={loading} style={{
          background: loading ? COLOR.card : `${COLOR.gold}22`,
          color: loading ? COLOR.dim : COLOR.gold,
          border: `1px solid ${loading ? COLOR.border : COLOR.goldDim || COLOR.gold + "55"}`,
          borderRadius: 5, padding: "3px 12px", fontSize: 13,
          fontWeight: 700, fontFamily: "monospace", letterSpacing: "0.04em",
          cursor: loading ? "not-allowed" : "pointer",
        }}>
          {loading ? "· Generando..." : "✦ Generar reporte"}
        </button>

        {reporte && (
          <button onClick={exportarHTML} disabled={exportando} title="Descargar informe como HTML" style={{
            background: exportando ? COLOR.card : `${COLOR.blue}18`,
            color: exportando ? COLOR.dim : COLOR.blue,
            border: `1px solid ${exportando ? COLOR.border : COLOR.blue + "55"}`,
            borderRadius: 5, padding: "3px 10px", fontSize: 13,
            fontWeight: 600, fontFamily: "monospace",
            cursor: exportando ? "not-allowed" : "pointer", flexShrink: 0,
          }}>
            {exportando ? "↓ Generando..." : "↓ HTML"}
          </button>
        )}
      </div>

      {/* ── ERROR ── */}
      {error && (
        <div style={{ background: `${COLOR.red}18`, color: COLOR.red, padding: "7px 18px", fontSize: 14, fontFamily: "monospace", borderBottom: `1px solid ${COLOR.red}44` }}>
          ▲ {error}
        </div>
      )}

      {/* ── ESTADO VACÍO / CARGANDO ── */}
      {!reporte && (
        <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column", gap: 10 }}>
          {loading ? (
            <>
              <div style={{ fontSize: 24, color: COLOR.gold, animation: "spin 1s linear infinite" }}>◈</div>
              <div style={{ fontSize: 15, color: COLOR.muted }}>Generando reporte · puede tardar ~30s</div>
            </>
          ) : (
            <>
              <div style={{ fontSize: 36, color: COLOR.gold + "44" }}>◈</div>
              <div style={{ fontSize: 17, fontWeight: 700, color: COLOR.text, letterSpacing: "0.05em" }}>CARTERA COMERCIAL IFRS 9</div>
              <div style={{ fontSize: 14, color: COLOR.muted }}>Selecciona el modo y periodo, luego haz clic en "Generar reporte"</div>
            </>
          )}
        </div>
      )}

      {/* ── REPORTE ── */}
      {reporte && (
        <div style={{ display: "flex", flexDirection: "column", flex: 1, overflow: "hidden" }}>
          {/* Meta */}
          <div style={{ padding: "6px 18px", background: COLOR.surface, borderBottom: `1px solid ${COLOR.border}`, fontSize: 13, color: COLOR.muted, fontFamily: "monospace" }}>
            {reporte.nombre_entidad} · Periodo {reporte.periodo} · {reporte.tiempo_s}s
          </div>

          {/* Tabs */}
          <div style={{ display: "flex", gap: 2, padding: "6px 12px", borderBottom: `1px solid ${COLOR.border}`, background: COLOR.surface, flexWrap: "wrap" }}>
            {SECCIONES.map(sec => {
              const active = seccionActiva === sec.id;
              const badge  = sec.id === "riesgo" ? numAnomalias : 0;
              return (
                <button key={sec.id} onClick={() => setSeccionActiva(sec.id)} style={{
                  padding: "4px 10px", borderRadius: 4, fontSize: 13,
                  fontFamily: "monospace", cursor: "pointer",
                  border: `1px solid ${active ? COLOR.gold : COLOR.border}`,
                  background: active ? `${COLOR.gold}18` : "transparent",
                  color: active ? COLOR.gold : COLOR.muted,
                  fontWeight: active ? 700 : 400,
                }}>
                  {sec.icon} {sec.label}
                  {badge > 0 && (
                    <span style={{ marginLeft: 4, background: `${COLOR.red}22`, color: COLOR.red, padding: "0 4px", borderRadius: 8, fontSize: 12 }}>{badge}</span>
                  )}
                </button>
              );
            })}
          </div>

          {/* Contenido de la sección activa */}
          <div style={{ flex: 1, overflowY: "auto", padding: "18px 20px", background: COLOR.bg }}>
            {seccionActiva === "resumen"    && <SeccionResumen    s={s} />}
            {seccionActiva === "calidad"    && <SeccionCalidad    s={s} />}
            {seccionActiva === "rollrates"  && <SeccionRollRates  s={s} />}
            {seccionActiva === "sectorial"  && <SeccionSectorial  s={s} />}
            {seccionActiva === "originacion"&& <SeccionOriginacion s={s} />}
            {seccionActiva === "territorio" && <SeccionTerritorio s={s} />}
            {seccionActiva === "riesgo"     && <SeccionRiesgo     s={s} />}
            {seccionActiva === "pronostico" && <SeccionPronostico s={s} />}
          </div>
        </div>
      )}
    </div>
  );
}
